// =============================================================================
// MemberDetailPage — View member profile & audit logs
// =============================================================================

import { Fragment, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, Avatar, Badge, Button } from '@tkpod/ui';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MemberDetail {
  id: string;
  handle: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    avatar: string | null;
    phone: string | null;
    status: string;
    emailVerified: boolean;
    lastLoginAt: string | null;
    lastLoginIp: string | null;
    createdAt: string;
  };
  role: { id: string; name: string; slug: string };
  isActive: boolean;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
}

interface ResolveHandleResponse {
  data: { memberId: string };
}

interface AuditLogEntry {
  id: string;
  action: string;
  actorType: string;
  entityType: string | null;
  entityId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  requestId: string | null;
  changes: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface AuditLogsResponse {
  data: AuditLogEntry[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAction(action: string): string {
  return action
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseUserAgent(ua: string | null): string {
  if (!ua) return '—';
  const browser =
    ua.match(/(?:Chrome|Firefox|Safari|Edge|Opera|MSIE|Trident)[/\s][\d.]+/)?.[0] ??
    '';
  const os =
    ua.match(/\(([^)]+)\)/)?.[1]?.split(';')[0]?.trim() ?? '';
  if (browser || os) return [browser, os].filter(Boolean).join(' · ');
  if (ua.length > 40) return ua.slice(0, 40) + '…';
  return ua;
}

function formatChanges(changes: Record<string, unknown> | null): string | null {
  if (!changes || Object.keys(changes).length === 0) return null;
  return Object.entries(changes)
    .map(([key, val]) => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
      if (val && typeof val === 'object' && 'from' in (val as Record<string, unknown>) && 'to' in (val as Record<string, unknown>)) {
        const diff = val as { from: unknown; to: unknown };
        return `${label}: ${String(diff.from)} → ${String(diff.to)}`;
      }
      return `${label}: ${String(val)}`;
    })
    .join(', ');
}

const AUDIT_LOG_PERMISSIONS = ['member:read', 'member:manage'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MemberDetailPage() {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const org = user?.currentOrganization;
  const orgId = org?.id;

  const stateId = (location.state as { memberId?: string } | null)?.memberId;

  const [logPage, setLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const logLimit = 15;

  const toggleExpand = useCallback((id: string) => {
    setExpandedLogId((prev) => (prev === id ? null : id));
  }, []);

  const permissions = org?.permissions ?? [];
  const canViewLogs = hasAnyPermission(permissions, AUDIT_LOG_PERMISSIONS);

  const resolveQuery = useQuery<ResolveHandleResponse>({
    queryKey: ['organizations', orgId, 'members', 'resolve-handle', handle],
    queryFn: () =>
      api.get<ResolveHandleResponse>(
        `/api/v1/organizations/${orgId}/members/resolve-handle/${encodeURIComponent(handle!)}`,
      ),
    enabled: !!orgId && !!handle && !stateId,
  });

  const memberId = stateId ?? resolveQuery.data?.data?.memberId;

  const memberQuery = useQuery<{ data: MemberDetail }>({
    queryKey: ['organizations', orgId, 'members', memberId, 'detail'],
    queryFn: () =>
      api.get<{ data: MemberDetail }>(
        `/api/v1/organizations/${orgId}/members/${memberId}/detail`,
      ),
    enabled: !!orgId && !!memberId,
  });

  const logsQuery = useQuery<AuditLogsResponse>({
    queryKey: ['organizations', orgId, 'members', memberId, 'audit-logs', logPage],
    queryFn: () =>
      api.get<AuditLogsResponse>(
        `/api/v1/organizations/${orgId}/members/${memberId}/audit-logs?page=${logPage}&limit=${logLimit}`,
      ),
    enabled: !!orgId && !!memberId && canViewLogs,
  });

  const member = memberQuery.data?.data;
  const displayName = member
    ? [member.user.firstName, member.user.lastName].filter(Boolean).join(' ') || member.user.email
    : '';

  if (!orgId) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        No organization found.
      </div>
    );
  }

  const isLoading = resolveQuery.isLoading || memberQuery.isLoading;
  const isError = resolveQuery.isError || memberQuery.isError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        <svg className="animate-spin h-5 w-5 text-brand-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Loading member details...
      </div>
    );
  }

  if (isError || !member) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          Failed to load member details.
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/settings/team')}>
          ← Back to Team
        </Button>
      </div>
    );
  }

  const logs = logsQuery.data?.data ?? [];
  const logsMeta = logsQuery.data?.meta;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate('/settings/team')}
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        Back to Team
      </button>

      {/* ================================================================= */}
      {/* Profile Info Card                                                  */}
      {/* ================================================================= */}
      <Card>
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <Avatar
            src={member.user.avatar}
            name={displayName}
            size="xl"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-text-primary truncate">
              {displayName}
            </h2>
            <p className="mt-0.5 text-sm text-text-secondary">{member.user.email}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-text-secondary">{member.role.name}</span>
              <span className="text-text-tertiary">·</span>
              {member.isActive ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success-light px-2 py-0.5 text-xs font-medium text-success-dark">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-success" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-light px-2 py-0.5 text-xs font-medium text-danger-dark">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-danger" />
                  Suspended
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ================================================================= */}
      {/* Details Card                                                       */}
      {/* ================================================================= */}
      <Card>
        <CardHeader title="Member Details" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailRow label="Email" value={member.user.email} />
          <DetailRow label="Phone" value={member.user.phone ?? '—'} />
          <DetailRow label="Role" value={member.role.name} />
          <DetailRow
            label="Email Verified"
            value={member.user.emailVerified ? 'Yes' : 'No'}
          />
          <DetailRow
            label="Account Created"
            value={formatDate(member.user.createdAt)}
          />
          <DetailRow
            label="Joined Organization"
            value={member.acceptedAt ? formatDate(member.acceptedAt) : '—'}
          />
          <DetailRow
            label="Last Login"
            value={member.user.lastLoginAt ? formatDate(member.user.lastLoginAt) : 'Never'}
          />
          <DetailRow
            label="Last Login IP"
            value={member.user.lastLoginIp ?? '—'}
          />
        </div>
      </Card>

      {/* ================================================================= */}
      {/* Activity / Audit Logs (owner, admin, manager only)                 */}
      {/* ================================================================= */}
      {canViewLogs && (
        <Card padding="none">
          <div className="p-6 pb-0">
            <CardHeader
              title="Activity Log"
              description="Recent actions performed by this member."
            />
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="w-8 px-2 py-3" />
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    IP Address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    User Agent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {logsQuery.isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-text-tertiary">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-text-tertiary">
                      No activity logs found for this member.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const isExpanded = expandedLogId === log.id;
                    const changesText = formatChanges(log.changes);
                    const hasExtra = !!changesText || !!log.metadata || !!log.requestId;

                    return (
                      <Fragment key={log.id}>
                        <tr
                          className={`transition-colors hover:bg-surface-secondary ${hasExtra ? 'cursor-pointer' : ''}`}
                          onClick={() => hasExtra && toggleExpand(log.id)}
                        >
                          <td className="w-8 px-2 py-3 text-center text-text-tertiary">
                            {hasExtra && (
                              <svg
                                className={`inline-block h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                              </svg>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge variant="default" size="sm">
                              {formatAction(log.action)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                            {log.entityType ? (
                              <span>
                                {log.entityType}
                                {log.entityId && (
                                  <span className="ml-1 text-text-tertiary font-mono text-xs">
                                    #{log.entityId.slice(-8)}
                                  </span>
                                )}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap font-mono">
                            {log.ipAddress ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap max-w-[200px] truncate" title={log.userAgent ?? undefined}>
                            {parseUserAgent(log.userAgent)}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                            {formatDate(log.createdAt)}
                          </td>
                        </tr>

                        {isExpanded && hasExtra && (
                          <tr className="bg-surface-secondary/50">
                            <td colSpan={6} className="px-6 py-3">
                              <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                                {changesText && (
                                  <div>
                                    <span className="font-semibold text-text-secondary">Changes:</span>
                                    <span className="ml-1 text-text-primary">{changesText}</span>
                                  </div>
                                )}
                                {log.metadata && Object.keys(log.metadata).length > 0 && (
                                  <div>
                                    <span className="font-semibold text-text-secondary">Metadata:</span>
                                    <span className="ml-1 text-text-primary font-mono">
                                      {JSON.stringify(log.metadata)}
                                    </span>
                                  </div>
                                )}
                                {log.requestId && (
                                  <div>
                                    <span className="font-semibold text-text-secondary">Request ID:</span>
                                    <span className="ml-1 text-text-primary font-mono">{log.requestId}</span>
                                  </div>
                                )}
                                {log.userAgent && (
                                  <div className="sm:col-span-2">
                                    <span className="font-semibold text-text-secondary">Full User Agent:</span>
                                    <span className="ml-1 text-text-primary font-mono break-all">{log.userAgent}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {logsMeta && logsMeta.total > 0 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-xs text-text-tertiary">
                Showing {Math.min((logPage - 1) * logLimit + 1, logsMeta.total)}–
                {Math.min(logPage * logLimit, logsMeta.total)} of {logsMeta.total} entries
              </span>
              {logsMeta.totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                    disabled={logPage === 1}
                    className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ‹ Prev
                  </button>
                  <span className="text-xs text-text-tertiary">
                    {logPage} / {logsMeta.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLogPage((p) => Math.min(logsMeta.totalPages, p + 1))}
                    disabled={logPage === logsMeta.totalPages}
                    className="rounded px-2 py-1 text-xs text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail row helper component
// ---------------------------------------------------------------------------

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-secondary px-4 py-3">
      <p className="text-xs font-medium text-text-tertiary">{label}</p>
      <p className="mt-1 text-sm text-text-primary truncate">{value}</p>
    </div>
  );
}
